// ─── Admin Money-Confirmation Box (تأكيد الأموال) ───
// Shows all admin-confirmed driver payments in a separate ledger,
// completely isolated from the capital wallet. Cards reset visually
// every 24h (default range = "today"). Supports day/week/month/custom
// range filters and a per-day archive history.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet,
  Loader2,
  Calendar,
  TrendingUp,
  Truck,
  User,
  Phone,
  Clock3,
  Archive,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#7C3AED"; // royal purple — matches the header icon
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

const RANGES = [
  { id: "today", label: "اليوم" },
  { id: "week", label: "الأسبوع" },
  { id: "month", label: "الشهر" },
  { id: "custom", label: "تواريخ مخصصة" },
];

const MoneyBoxTab = () => {
  const [range, setRange] = useState("today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyDays, setHistoryDays] = useState(30);
  const [expanded, setExpanded] = useState({}); // day → bool (currently only mass open)
  const [showHistory, setShowHistory] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const sumParams = { range };
      if (range === "custom" && from && to) {
        sumParams.date_from = from;
        sumParams.date_to = to;
      }
      const [sumRes, histRes] = await Promise.all([
        axios.get(`${API}/admin/money-box/summary`, { params: sumParams }),
        axios.get(`${API}/admin/money-box/history`, { params: { days: historyDays } }),
      ]);
      setSummary(sumRes.data);
      setHistory(histRes.data || []);
    } catch (err) {
      toast.error("فشل تحميل صندوق تأكيد الأموال");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range, historyDays]);

  const total = Number(summary?.total || 0);
  const entries = summary?.entries || [];

  // Group entries by day_key for nicer display inside the active range.
  const grouped = useMemo(() => {
    const m = {};
    entries.forEach((e) => {
      const k = e.day_key || (e.confirmed_at || "").slice(0, 10);
      if (!m[k]) m[k] = [];
      m[k].push(e);
    });
    return Object.entries(m).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [entries]);

  return (
    <div className="space-y-3" data-testid="money-box-tab" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#4C1D95] to-[#A855F7] text-white rounded-2xl p-4 shadow-xl">
        <div className="flex items-center gap-2 mb-1">
          <Wallet className="w-5 h-5" />
          <h3 className="font-black text-lg">صندوق تأكيد الأموال</h3>
        </div>
        <p className="text-xs opacity-90">
          الأموال هنا من تأكيدات استلام نقد من السائقين — <b>منفصلة تماماً عن رأس المال</b>. يتصفّر العرض كل 24 ساعة ويُحفظ في الأرشيف.
        </p>
      </div>

      {/* Range filter */}
      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 bg-[#F3F6F4] rounded-full p-0.5 flex-wrap">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRange(r.id)}
              data-testid={`money-box-range-${r.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                range === r.id ? "text-white" : "text-[#5C6E64] hover:bg-white"
              }`}
              style={range === r.id ? { background: ACCENT } : undefined}
            >
              {r.label}
            </button>
          ))}
        </div>
        {range === "custom" && (
          <div className="flex items-center gap-1 text-xs">
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="money-box-from"
              className="px-2 py-1.5 rounded-lg border border-[#E4EBE6]"
              dir="ltr"
            />
            <span>إلى</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="money-box-to"
              className="px-2 py-1.5 rounded-lg border border-[#E4EBE6]"
              dir="ltr"
            />
            <button
              type="button"
              onClick={fetchAll}
              data-testid="money-box-apply"
              className="text-white font-bold rounded-full px-3 py-1.5"
              style={{ background: ACCENT }}
            >
              تطبيق
            </button>
          </div>
        )}
      </div>

      {/* Headline KPI */}
      {loading || !summary ? (
        <div className="py-10 text-center"><Loader2 className="w-7 h-7 animate-spin inline" style={{ color: ACCENT }} /></div>
      ) : (
        <>
          <div className="bg-white rounded-2xl p-5 border-2 shadow-md" style={{ borderColor: `${ACCENT}33` }}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs text-[#5C6E64] flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  مجموع المؤكَّد ({RANGES.find((r) => r.id === range)?.label || ""})
                </p>
                <p className="font-black text-4xl mt-1" style={{ color: ACCENT }} dir="ltr">
                  {fmt(total)} <span className="text-xl opacity-70">د.ع</span>
                </p>
                <p className="text-[10px] text-[#5C6E64] mt-1">{summary.count} عملية تأكيد</p>
              </div>
              <TrendingUp className="w-16 h-16 opacity-20" style={{ color: ACCENT }} />
            </div>
          </div>

          {/* Entries grouped by day */}
          {entries.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-[#E4EBE6]">
              <Wallet className="w-12 h-12 mx-auto text-[#5C6E64] mb-2 opacity-50" />
              <p className="text-sm text-[#5C6E64]">لا توجد عمليات تأكيد ضمن هذه الفترة.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {grouped.map(([day, list]) => {
                const dayTotal = list.reduce((s, x) => s + Number(x.amount || 0), 0);
                return (
                  <div key={day} className="bg-white rounded-2xl border-2 border-[#E4EBE6] overflow-hidden">
                    <div className="flex justify-between items-center px-3 py-2 bg-[#F8FAFC]" style={{ borderRight: `4px solid ${ACCENT}` }}>
                      <p className="font-bold text-sm text-[#1E3A5F]" dir="ltr">{day}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#5C6E64]">{list.length} عملية</span>
                        <span className="font-black text-sm" style={{ color: ACCENT }} dir="ltr">{fmt(dayTotal)} د.ع</span>
                      </div>
                    </div>
                    <div className="divide-y divide-[#F3F6F4]">
                      {list.map((e) => (
                        <div key={e.id} className="px-3 py-2 flex items-center justify-between gap-2 text-xs">
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-[#111814] truncate flex items-center gap-1">
                              <User className="w-3 h-3 text-[#5C6E64]" /> {e.user_name}
                              {e.market_name && <span className="text-[#5C6E64]"> · {e.market_name}</span>}
                            </p>
                            <p className="text-[10px] text-[#5C6E64] flex items-center gap-2 mt-0.5">
                              {e.driver_name && (
                                <span className="flex items-center gap-0.5">
                                  <Truck className="w-3 h-3" /> {e.driver_name}
                                </span>
                              )}
                              <span className="flex items-center gap-0.5" dir="ltr">
                                <Clock3 className="w-3 h-3" />
                                {new Date(e.confirmed_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                              </span>
                              {e.invoice_number && (
                                <span className="bg-[#F0F9FF] text-[#1E3A5F] rounded-full px-1.5">#{e.invoice_number}</span>
                              )}
                            </p>
                          </div>
                          <p className="font-black text-sm shrink-0" style={{ color: ACCENT }} dir="ltr">
                            {fmt(e.amount)} د.ع
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Archive History (per-day totals over last N days) */}
          <div className="bg-white rounded-2xl border-2 border-[#E4EBE6] overflow-hidden">
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              data-testid="money-box-history-toggle"
              className="w-full flex items-center justify-between px-3 py-3 bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white"
            >
              <div className="flex items-center gap-2">
                <Archive className="w-4 h-4" />
                <h4 className="font-bold text-sm">السجل اليومي (أرشيف الأيام السابقة)</h4>
              </div>
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showHistory && (
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs text-[#5C6E64]">آخر {historyDays} يوم — كل يوم 24 ساعة منفصل</p>
                  <div className="flex gap-1">
                    {[7, 30, 90, 180].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setHistoryDays(d)}
                        data-testid={`money-box-history-${d}`}
                        className={`text-xs font-bold rounded-full px-2.5 py-1 ${historyDays === d ? "text-white" : "bg-[#F3F6F4] text-[#5C6E64]"}`}
                        style={historyDays === d ? { background: ACCENT } : undefined}
                      >
                        {d} يوم
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-96 overflow-y-auto">
                  {history.map((h) => {
                    const isToday = h.day === new Date().toISOString().slice(0, 10);
                    const zero = h.total === 0;
                    return (
                      <div
                        key={h.day}
                        data-testid={`money-box-day-${h.day}`}
                        className={`rounded-xl p-2 border-2 ${
                          isToday ? "bg-[#F3E8FF] border-[#7C3AED]" : zero ? "bg-[#F8FAFC] border-[#E4EBE6]" : "bg-white border-[#E4EBE6]"
                        }`}
                      >
                        <p className="text-[10px] text-[#5C6E64]" dir="ltr">{h.day}{isToday && " · اليوم"}</p>
                        <p className={`font-black text-base mt-0.5 ${zero ? "text-[#9CA3AF]" : ""}`} style={!zero ? { color: ACCENT } : undefined} dir="ltr">
                          {fmt(h.total)}
                        </p>
                        <p className="text-[9px] text-[#5C6E64]">{h.count} عملية</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MoneyBoxTab;
